import React, {useEffect, useRef, useState} from 'react';
import './index.scss';
import {Helmet} from 'react-helmet';
import DashboardHeader from '../../components/DashboardHeader';
import Wrapper from '../../components/Wrapper';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import Pencil from '../../SVG/Pencil';
import Trash from '../../SVG/Trash';
import SimpleModal from '../../components/SimpleModal';
import {
  useDeleteCustomInspectionCategoryMutation,
  useLazyGetCustomCategoriesQuery,
  useLazyGetCustomInspectionsDetailsQuery,
} from '../../store/builder/inspections.api';
import CustomInspectionsBuildModal from '../../components/CustomInpections/BuildInspectionModal';
import {setDefaultState} from '../CreateNewForm/service/createForm.slice';
import {useDispatch} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import moment from 'moment';
import {toast} from 'react-toastify';
import useLoader from '../../global/UseLoadingHandler';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {useTranslation} from '../../hooks/useTranslation';

const CustomInspectionsDetails = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();
  const userType = location.state?.userType;
  const [rows, setRows] = useState([]);
  const [deleteModal, setDeleteForm] = useState(false);
  const [buildInspection, setBuildInspection] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const navigate = useNavigate();
  
  // Pagination state
  const loaderRef = useRef(null);
  const [formsListForModal, setFormsListForModal] = useState([]);
  const [formsPage, setFormsPage] = useState(1);

  const [
    getCustomsDetails,
    {
      data: getCustomsDetailsData,
      isError: getCustomsDetailsIsError,
      isLoading: getCustomsDetailsIsLoading,
      error: getCustomsDetailsError,
      isSuccess: getCustomsDetailsIsSuccess,
    },
  ] = useLazyGetCustomInspectionsDetailsQuery();

  const [
    removeCategory,
    {
      data: removeCategoryData,
      isError: removeCategoryIsError,
      isLoading: removeCategoryIsLoading,
      error: removeCategoryError,
      isSuccess: removeCategoryIsSuccess,
    },
  ] = useDeleteCustomInspectionCategoryMutation();

  useLoader([getCustomsDetailsIsLoading, removeCategoryIsLoading]);

  const [errorHandler, setErrorHandler] = useErrorHandler([
    getCustomsDetailsError,
    removeCategoryError,
  ]);

  const [getFormsList, {data: formsData, isFetching: formsIsFetching, isSuccess: formsIsSuccess}] = useLazyGetCustomCategoriesQuery();

  useEffect(() => {
    getFormsList({page: formsPage, is_corporate: false}).unwrap();
  }, [formsPage]);
  
  useEffect(() => {
    getCustomsDetails(params?.id);
  }, []);
  
  useEffect(() => {
    if (formsIsSuccess && formsData) {
      setFormsListForModal(prevItems => [...prevItems, ...formsData?.data]);
    }
  }, [formsData, formsIsSuccess]);

  useEffect(() => {
    if (getCustomsDetailsData?.categories?.length > 0) {
      setRows(getCustomsDetailsData?.categories);
    } else {
      setRows([]);
    }
  }, [getCustomsDetailsData]);

  useEffect(() => {
    return window.addEventListener('click', e => {
      if (
        e.target.closest('.custom__table-settings') === null &&
        e.target.closest('.custom__table__dropdown') === null
      ) {
        setRows(old => {
          let data = old.map(item => {
            return {...item, action_status: false};
          });
          return data;
        });
      }
    });
  }, [location]);

  const clickSettings = (e, row, v) => {
    e.stopPropagation();
    setRows(old => {
      let data = old.map(item => {
        if (item?.id === row?.id) {
          return {...item, action_status: v}; // Create a new object with the updated action_status
        }
        return item;
      });
      return data;
    });
  };

  const deleteForm = () => {
    removeCategory(currentForm)
      .unwrap()
      .then(res => {
        toast.success(t('customInspectionsDetails.messages.formDeletedSuccess'));
        setDeleteForm(false);
        getCustomsDetails(params?.id);
      })
      .catch(err => {
        setDeleteForm(false);
      });
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('customInspectionsDetails.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('customInspectionsDetails.title')}
        type={location.state?.hiddenBtn ? '' : 'custom-inspections-forms'}
        custom_inspection_name={getCustomsDetailsData?.name}
        action={() => {
          navigate(`/custom-inspections/edit-inspection/${params?.id}`, {
            state: {
              readOnlyFromCorporate:
                getCustomsDetailsData?.parent_id && userType === 'admin'
                  ? true
                  : false,
            },
          });
        }}
      />
      {buildInspection && (
        <CustomInspectionsBuildModal
          exit={() => {
            setBuildInspection(false);
          }}
          createAction={() => {
            dispatch(setDefaultState());
            // navigate('/custom-inspections/create-form', {state: {page: 'build-inspection'}})
          }}
          items={formsListForModal}
          // selectedItems={state.category_ids.value}
          setSelectedItems={v => {
            // setState(old => ({...old, category_ids: {...old.category_ids, value: v}}))
          }}
          title={t('customInspectionsBuildModal.title')}
          itemTitle={t('customInspectionsBuildModal.itemTitles.forms')}
          actionTitle={t('customInspectionsBuildModal.itemTitles.inspectionItems')}
          newTitle={t('customInspectionsBuildModal.itemTitles.form')}
          type={'form'}
          loaderRef={loaderRef}
          isFetching={formsIsFetching}
          setPage={setFormsPage}
          maxPage={formsData?.pages}
          currentPage={formsPage}
        />
      )}
      {deleteModal && (
        <SimpleModal
          title={t('customInspectionsDetails.modals.deleteTitle')}
          exit={() => {
            setDeleteForm(false);
          }}
          submitAction={() => deleteForm()}
          submitTitle={t('actions.delete')}
          isDelete={true}
        >
          {t('customInspectionsDetails.modals.deleteMessage')}
        </SimpleModal>
      )}
      <div className='customForm'>
        <div className='customForm__content'>
          <div className='customForm__table'>
            <div className='customForm__table-header'>
              <div className='customForm__table-th'>{t('customInspectionsDetails.tableHeaders.name')}</div>
              <div className='customForm__table-th'>{t('customInspectionsDetails.tableHeaders.dateOfCreation')}</div>
              <div className='customForm__table-th'>{t('customInspectionsDetails.tableHeaders.createdBy')}</div>
              <div className='customForm__table-th'></div>
            </div>
            <div className='customForm__table-body'>
              {rows?.length > 0 ? (
                rows?.map((row, index) => (
                  <div key={index} style={{position: 'relative'}}>
                    <div className={`customForm__table-row`}>
                      <div className='customForm__table-bodyTh'>
                        {row?.name}
                      </div>
                      <div className='customForm__table-bodyTh'>
                        {moment(row?.created_at).format('MM/DD/YYYY')}
                      </div>
                      <div className='customForm__table-bodyTh'>
                        {row?.created_by?.name}
                      </div>
                      <div className='custom__table-bodyTh'>
                        {userType === 'admin' &&
                        !getCustomsDetailsData?.parent_id ? null : (
                          <img
                            className='custom__table-settings'
                            src='/assets/icons/dots.svg'
                            alt='dots'
                            onClick={e =>
                              clickSettings(e, row, !row?.action_status)
                            }
                          />
                        )}
                      </div>
                    </div>
                    {row?.action_status && (
                      <div className='custom__table__dropdown'>
                        <div
                          className='custom__table__dropdown-elem'
                          onClick={() => {
                            if (getCustomsDetailsData?.parent_id) {
                              navigate(
                                `/forms/edit-form/${row?.id}?details=true`,
                              );
                            } else {
                              navigate(`/forms/edit-form/${row?.id}`);
                            }
                          }}
                        >
                          <Pencil color={'#999999'} />
                          <span>
                            {getCustomsDetailsData?.parent_id
                              ? t('customInspectionsDetails.actions.details')
                              : t('customInspectionsDetails.actions.edit')}{' '}
                            {t('customInspectionsDetails.actions.form')}
                          </span>
                        </div>
                        {getCustomsDetailsData?.parent_id &&
                        userType === 'admin' ? null : (
                          <div
                            className='custom__table__dropdown-elem'
                            onClick={() => {
                              setDeleteForm(true);
                              setCurrentForm(row?.id);
                            }}
                          >
                            <Trash color={'#999999'} />
                            <span>{t('customInspectionsDetails.actions.deleteForm')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '205px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '100px 0px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <div className='expired__box-img' style={{margin: '0'}}>
                      <img src='/assets/no-data.svg' alt='no-data' />
                    </div>
                    <p className='no-payment-text'>{t('customInspectionsDetails.messages.noData')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default CustomInspectionsDetails;
